import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import {
  Modal, Button, FormGroup, ControlLabel, FormControl,
  HelpBlock,
} from 'react-bootstrap';
import { orderBy, filter, concat, reduce, find } from 'lodash';
import Fa from 'react-fontawesome';
import Toggle from 'react-bootstrap-toggle';
import Joi from 'joi';
import ProgressBar from '../../../../components/progress-bar';
import { Field, reduxForm, formValueSelector, SubmissionError } from 'redux-form';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import RecipientAutocomplete from '../DiscoveryBotPage/RecipientAutocomplete';
import { getSyncSettings, updateSyncSettings } from '../../../Admin/AdminActions';

class SyncNotificationPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loaded: false,
      recipients: [],
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.render = this.render.bind(this);
    this.onToggleEnable = this.onToggleEnable.bind(this);
    this.onToggleShouldReportError = this.onToggleShouldReportError.bind(this);
    this.close = this.close.bind(this);
    this.setRecipients = this.setRecipients.bind(this);
    this.handleRecipientChange = this.handleRecipientChange.bind(this);
    this.onToggleShouldCheckSyncToken = this.onToggleShouldCheckSyncToken.bind(this);
    this.renderField = this.renderField.bind(this);
  }

  componentDidMount() {
    this.mounted = true;
    const { organization } = this.props;
    if (organization) {
      this.props.dispatch(getSyncSettings(organization._id))
        .then(
          (params) => {
            if (this.mounted) {
              this.props.initialize({ 
                ...params,
              });
              this.setRecipients(params.$to_others || []);
              this.setState({ loaded: true });
            }
          },
          (e) => {
            // TODO: handle error
          }
        );
        
    }
  }

  setRecipients(recipients) {
    const users = orderBy(filter(recipients, r => !r.isEmail), ['first_name', 'last_name'], ['asc', 'asc']);
    const emails = orderBy(filter(recipients, r => r.isEmail), ['email'], ['asc']);
    this.setState({ recipients: concat(users, emails) });
  }

  renderField({
      input, label, type, meta: { touched, error }, readOnly, disabled, options, componentClass,
    }) {
      const validationState = touched && error ? 'error' : null;
      const attrs = {};
      if (readOnly) {
        attrs.readOnly = true;
      }
      if (disabled) {
        attrs.disabled = true;
      }
      return (
        <FormGroup validationState={validationState}>
          <ControlLabel>{label}</ControlLabel>
          {componentClass !== 'select' && <FormControl {...attrs} {...input} placeholder={label} type={type} />}
          {componentClass === 'select' &&
            <FormControl
              {...input}
              {...attrs}
              componentClass={componentClass}
            >
              <option>{label}</option>
              {options.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </FormControl>
          }
          <FormControl.Feedback />
          {touched && error && <HelpBlock className="text-left">{error}</HelpBlock>}
        </FormGroup>
      );
    }
  
  

  handleRecipientChange(recipients) {
    const cc = reduce(recipients, (items, recipient) => {
      if (recipient.isEmail) {
        items.push(recipient.email);
      } else {
        items.push(recipient._id);
      }
      return items;
    }, []);
    this.props.change('to_others', cc);
    this.setRecipients(recipients);
  }


  componentWillUnmount() {
    this.mounted = false;
  }

  close() {
    this.props.onClose();
  }

  handleSubmit(data) {
    const { 
      organization, syncNotificationDisable,
      shouldReportError,
      toAdmins, toManagers, toPI, shouldCheckSyncToken, checkSyncTokenPeriod,
    } = this.props;
    const params = {
      sync_notification_disable: syncNotificationDisable,
      enabled: shouldReportError,
      to_admins: toAdmins,
      to_managers: toManagers,
      to_pi: toPI,
      to_others: data.to_others || [],
      check_sync_token: shouldCheckSyncToken,
      ...(shouldCheckSyncToken ? { check_sync_token_period: checkSyncTokenPeriod } : {}),
    };
    const p = this.props.dispatch(updateSyncSettings(organization._id, params));
    p.then(
      (results) => {
        if (this.props.isModal) {
          this.close();
        } else {
          this.props.initialize({ 
            ...results,
          });
          this.setRecipients(results.$to_others || []);
        }
      },
      (error) => {
        const el = (<div>Failed to update sync notification settings. {error.message || ''}</div>);
        this.context.addNotification(el, { type: 'error', autoClose: true });
      },
    );
    return p;
  }

  onToggleEnable() {
    const { syncNotificationDisable } = this.props;
    this.props.change('sync_notification_disable', !syncNotificationDisable);
  }

  onToggleShouldReportError() {
    const { shouldReportError } = this.props;
    this.props.change('enabled', !shouldReportError);
  }

  onToggleShouldCheckSyncToken() {
    const { shouldCheckSyncToken } = this.props;
    this.props.change('check_sync_token', !shouldCheckSyncToken);
  }

  renderInline() {
    const { loaded } = this.state;
    const { 
      organization,
      syncNotificationDisable, submitting,
      shouldReportError, shouldCheckSyncToken, checkSyncTokenPeriod,
      toAdmins, toManagers, toPI, 
    } = this.props;
    if (!loaded) {
      return (<ProgressBar />);
    }

    const options = [
      {
        name: 'to_admins',
        value: toAdmins,
        label: `${organization.display_name} Admins`,
      },
      {
        name: 'to_managers',
        value: toManagers,
        label: `${organization.display_name} Managers`,
      },
      {
        name: 'to_pi',
        value: toPI,
        label: 'TruVoice Support',
      },
    ];

    const periodOptions = [
      { value: 7, label: 'One Week' },
      { value: 14, label: 'Two Weeks' },
      { value: 21, label: 'Three Weeks' },
      { value: 30, label: 'One Month' },
      { value: 60, label: 'Two Months' },
      { value: 90, label: 'Three Months' },
      { value: 120, label: 'Four Months' },
      { value: 150, label: 'Five Months' },
      { value: 180, label: 'Six Months' },
    ];
    const selectedPeriodOptions = find(periodOptions, { value: checkSyncTokenPeriod });

    return (
      <div className="panel panel-default">
        <div className="panel-heading">
          Sync Notification Settings:
        </div>
        <div className="panel-body">
          <FormGroup>
            <ControlLabel>Enable Sync Notification:</ControlLabel>
            <div>
              <Toggle
                onClick={this.onToggleEnable}
                on={<span>Enabled</span>}
                off={<span>Disabled</span>}
                size="xs"
                onstyle="success"
                offstyle="danger"
                active={!syncNotificationDisable}
              />
            </div>
            <div>If enabled this will send out sync statistics emails.</div>
          </FormGroup>
          <hr />
          
          <hr />
          <FormGroup>
            <ControlLabel>Enable Expired Sync Token Reporting:</ControlLabel>
            <div>
              <Toggle
                onClick={this.onToggleShouldCheckSyncToken}
                on={<span>Enabled</span>}
                off={<span>Disabled</span>}
                size="xs"
                onstyle="success"
                offstyle="danger"
                active={shouldCheckSyncToken}
              />
            </div>
            <div>If enabled this will send out notification emails when there are errors due to sync token expired.</div>

          </FormGroup>
          {shouldCheckSyncToken &&
            <div>
              <Field disabled={submitting} options={periodOptions} name="check_sync_token_period" component={this.renderField} label="Remind Period" componentClass="select" />
              <div>Send a reminder email if the integrate user has not logged in more than {selectedPeriodOptions?.label.toLowerCase()}</div>
            </div>
          }

          <hr />
          <FormGroup>
            <ControlLabel>Enable Missing Field Errors Reporting:</ControlLabel>
            <div>
              <Toggle
                onClick={this.onToggleShouldReportError}
                on={<span>Enabled</span>}
                off={<span>Disabled</span>}
                size="xs"
                onstyle="success"
                offstyle="danger"
                active={shouldReportError}
              />
            </div>
            <div>If enabled this will send out notification emails when there are errors due to missing fields</div>

          </FormGroup>

          

          
          <FormGroup>
            <ControlLabel>Send Error Report To:</ControlLabel>
            <ul>
              {options.map((option) => {
                const checked = option.value === true;
                const style = {
                  cursor: submitting ? 'not-allowed' : 'pointer',
                };
                return (
                  <li 
                    key={option.name} 
                    style={style}
                    onClick={() => {
                      if (!this.props.submitting) {
                        this.props.change(option.name, !option.value);
                      }
                    }}
                  >
                    <Fa name={checked ? 'check-square' : 'square-o'} className={checked ? 'text-active' : ''} /> {option.label}
                  </li>
                );
              })}
              <li>
                <ControlLabel>Others:</ControlLabel>
                <RecipientAutocomplete
                  organization={organization}
                  recipients={this.state.recipients}
                  submitting={submitting}
                  onChange={this.handleRecipientChange}
                />
              </li>
            </ul>
          </FormGroup>

        </div>
        <div className="panel-footer text-right">
          <Button disabled={submitting} bsStyle="primary" type="submit">
            {!submitting && <span>Save</span>}
            {submitting && <span>Saving</span>}
          </Button>
        </div>
      </div>
    );
  }

  renderForm() {
    const { handleSubmit, organization } = this.props;
    return (
      <form onSubmit={handleSubmit(this.handleSubmit)} key={organization._id}>
        {this.renderInline()}
        <div style={{ height: 200 }} />
      </form>
    );
  }

  renderModal() {
    const { handleSubmit, submitting, organization, showModal } = this.props;
    const name = organization ? organization.display_name : '';

    return (
      <Modal show={showModal} onHide={this.close}>
        <form onSubmit={handleSubmit(this.handleSubmit)}>
          <Modal.Header closeButton>
            <Modal.Title>{name} | Sync Notification Settings:</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {this.renderInline()}
          </Modal.Body>
          <Modal.Footer>
            <Button disabled={submitting} bsStyle="primary" type="submit">
              {!submitting && <span>Save</span>}
              {submitting && <span>Saving</span>}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    );
  }

  render() {
    if (this.props.isModal) {
      return this.renderModal();
    }

    return (
      <div>
        <Helmet title="Sync Notification Settings | Organization Setup" />
        <div className="container">
          {this.renderForm()}
        </div>
      </div>
    );
  }
}

const selector = formValueSelector('syncNotificationForm');
function mapStateToProps(state) {
  return {
    organization: getUserCurrentOrganization(state),
    syncNotificationDisable: selector(state, 'sync_notification_disable'),
    shouldCheckSyncToken: selector(state, 'check_sync_token'),
    checkSyncTokenPeriod: parseInt(selector(state, 'check_sync_token_period'), 10),
    shouldReportError: selector(state, 'enabled'), // should send email report
    toPI: selector(state, 'to_pi'),
    toAdmins: selector(state, 'to_admins'),
    toManagers: selector(state, 'to_managers'),
    toOthers: selector(state, 'to_others'),
  };
}

SyncNotificationPage.propTypes = {
  organization: PropTypes.shape({
    is_demo: PropTypes.bool,
    type: PropTypes.string,
  },
  ).isRequired,
  initialize: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  handleSubmit: PropTypes.func,
  change: PropTypes.func,
  reset: PropTypes.func,
  onUpdate: PropTypes.func,
  onClose: PropTypes.func,
  error: PropTypes.string,
  valid: PropTypes.bool,
  syncNotificationDisable: PropTypes.bool,
  shouldCheckSyncToken: PropTypes.bool,

  shouldReportError: PropTypes.bool,
  toPI: PropTypes.bool,
  toAdmins: PropTypes.bool,
  toManagers: PropTypes.bool,
  toOthers: PropTypes.arrayOf(PropTypes.string),
  checkSyncTokenPeriod: PropTypes.number,
};

SyncNotificationPage.defaultProps = {
  onUpdate: () => {},
  onClose: () => {},
  change: () => {},
  handleSubmit: () => {},
  syncNotificationDisable: false,
  error: null,
  valid: false,
  shouldCheckSyncToken: false,
  checkSyncTokenPeriod: 30,
};
SyncNotificationPage.contextTypes = {
  addNotification: PropTypes.func.isRequired,
};

function validate(values) {
  const schema = Joi.object({
    enabled: Joi.boolean()
      .required()
      .messages({
        'any.required': 'Enabled setting is required',
        'boolean.base': 'Enabled must be a boolean',
      }),
    to_admins: Joi.boolean()
      .required()
      .messages({
        'any.required': 'To admins setting is required',
        'boolean.base': 'To admins must be a boolean',
      }),
    to_managers: Joi.boolean()
      .required()
      .messages({
        'any.required': 'To managers setting is required',
        'boolean.base': 'To managers must be a boolean',
      }),
    to_others: Joi.array()
      .items(Joi.string().email({ tlds: { allow: false } }))
      .optional()
      .messages({
        'array.base': 'To others must be an array',
      }),
    sync_notification_disable: Joi.boolean()
      .optional()
      .messages({
        'boolean.base': 'Sync notification disable must be a boolean',
      }),
    check_sync_token: Joi.boolean()
      .required()
      .messages({
        'boolean.base': 'Check sync token must be a boolean',
      }),
    check_sync_token_period: Joi.number().integer().greater(6)
      .required()
      .messages({
        'any.required': 'Check sync token period is required',
        'number.base': 'Check sync token period must be a number',
      }),
  }).unknown(true);
  const { error } = schema.validate(values, { abortEarly: false });
  if (!error) {
    return {};
  }
  const errors = {};
  error.details.forEach((err) => {
    const key = err.path[0];
    errors[key] = err.message;
  });
  return errors;
}

const SyncNotificationForm = reduxForm({
  form: 'syncNotificationForm',
  validate,
})(SyncNotificationPage);
export default connect(mapStateToProps)(SyncNotificationForm);
